import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Image, Briefcase, ImageIcon, Package, DollarSign, MessageSquare, Mail, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Services',
      value: stats?.services || 0,
      icon: Briefcase,
      color: 'from-blue-500 to-blue-600',
      link: '/admin/services'
    },
    {
      title: 'Galerie',
      value: stats?.gallery || 0,
      icon: ImageIcon,
      color: 'from-purple-500 to-purple-600',
      link: '/admin/gallery'
    },
    {
      title: 'Location',
      value: stats?.rentals || 0,
      icon: Package,
      color: 'from-green-500 to-green-600',
      link: '/admin/rentals'
    },
    {
      title: 'Forfaits',
      value: stats?.packages || 0,
      icon: DollarSign,
      color: 'from-yellow-500 to-yellow-600',
      link: '/admin/packages'
    },
    {
      title: 'Témoignages',
      value: stats?.testimonials || 0,
      icon: MessageSquare,
      color: 'from-pink-500 to-pink-600',
      link: '/admin/testimonials'
    },
    {
      title: 'Demandes en attente',
      value: stats?.quotes?.pending || 0,
      icon: Mail,
      color: 'from-red-500 to-red-600',
      link: '/admin/quotes'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tableau de bord</h1>
        <p className="text-gray-600">Vue d'ensemble de votre site web</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.link}>
              <Card className="hover:shadow-xl transition-shadow cursor-pointer">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  <p className="text-xs text-gray-500 mt-1">Cliquez pour gérer</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Actions rapides</CardTitle>
            <CardDescription>Raccourcis vers les tâches courantes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link
              to="/admin/media"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Image className="w-5 h-5 text-purple-600" />
              <span>Uploader une image</span>
            </Link>
            <Link
              to="/admin/services"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Briefcase className="w-5 h-5 text-blue-600" />
              <span>Ajouter un service</span>
            </Link>
            <Link
              to="/admin/gallery"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ImageIcon className="w-5 h-5 text-purple-600" />
              <span>Ajouter à la galerie</span>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Activité récente</CardTitle>
            <CardDescription>Demandes de devis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-full bg-red-100">
                  <Mail className="w-4 h-4 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{stats?.quotes?.pending || 0} demande(s) en attente</p>
                  <p className="text-xs text-gray-500">Nécessite votre attention</p>
                </div>
                <Link to="/admin/quotes" className="text-sm text-purple-600 hover:underline">
                  Voir →
                </Link>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-full bg-green-100">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{stats?.quotes?.total || 0} demande(s) au total</p>
                  <p className="text-xs text-gray-500">Depuis le lancement</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
